import React, { useState } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ClipboardListCheckIcon, GearIcon, BagShoppingIcon } from '@revfluence/fresh-icons/regular/esm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@frontend/shadcn/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { NonShopifyCollection } from './NonShopifyCollection';

interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

const tabs: TabItem[] = [
  {
    id: 'collections',
    label: 'Collections',
    content: <NonShopifyCollection />
  }
];

export const Products = () => {
  const [activeTab, setActiveTab] = useState('collections');

  const getTabClassName = (tabId: string) => {
    const isActive = activeTab === tabId;
    return `flex flex-col items-start px-0 py-0 rounded-none data-[state=active]:shadow-none data-[state=active]:bg-transparent hover:bg-transparent bg-transparent ${isActive ? 'font-medium text-grey-6' : 'font-normal text-grey-5'
      }`;
  };

  const getIndicatorClassName = (tabId: string) => {
    const isActive = activeTab === tabId;
    return `self-stretch w-full h-0.5 bg-primary mt-0.5 ${isActive ? 'opacity-100' : 'opacity-0'}`;
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full w-full">
        <div className="flex items-start justify-between mb-6">
          <div className="flex flex-col">
            <h1 className="text-lg font-medium text-primary mb-2">Products</h1>
            <p className="text-sm text-grey-5 m-0">
              View, add, update, and organize your collection, products and variants imported into Aspire.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outlineHeader"
                  size="icon"
                  className="p-2 text-grey-6 hover:text-grey-9 hover:bg-grey-1 rounded-md transition-colors"
                  title="Import Logs"
                >
                  <ClipboardListCheckIcon className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
              >
                <span>Import Logs</span>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outlineHeader"
                  size="icon"
                  className="p-2 text-grey-6 hover:text-grey-9 hover:bg-grey-1 rounded-md transition-colors"
                  title="Settings"
                >
                  <GearIcon className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
              >
                <span>Settings</span>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="flex items-center gap-2 px-4 py-2 bg-primary text-white hover:bg-gray-800 transition-colors">
                  <BagShoppingIcon className="w-4 h-4" />
                  <span>Upload CSV</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent
                className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
              >
                <p>Upload CSV</p>
              </TooltipContent>
            </Tooltip>

          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full bg-white flex-1 flex flex-col"
        >
          <TabsList className="h-auto bg-transparent border-b border-gray-200 p-0 gap-6 rounded-none w-full justify-start">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className={getTabClassName(tab.id)}
              >
                <span className="text-base font-medium">{tab.label}</span>
                <div className={getIndicatorClassName(tab.id)} />
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="flex-1 mt-0">
              <div className="h-full overflow-y-auto pt-6">
                {tab.content}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </TooltipProvider>

  );
};
